import { useT } from '@/store';
import { Modal } from '@/components/ui/Overlay';
import { ErrorState, SkeletonRows, EmptyState } from '@/components/ui/Panel';
import { useClientReliability } from '@/hooks/useClientReliability';

/**
 * Dispatcher-facing (this console, not the client portal): for each client
 * site, what fraction of its recent deliveries were independently
 * GPS-confirmed at arrival versus merely asserted by a manual status tap —
 * see blueprints/reporting.py's client_reliability() docstring for the
 * window and the low-sample rule this table has to respect. Opened from the
 * Clients/Sites toolbar (SiteList.jsx), same open/close/loading/error/
 * empty-state pattern as GpsFleetReportModal.jsx.
 */
export function ClientReliabilityModal({ open, onClose }) {
  const t = useT();
  const { data, isLoading, isError, error } = useClientReliability();
  const rows = data?.rows ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('clientReliability.title')}
      width="max-w-[720px]"
      footer={
        <button type="button" className="btn" onClick={onClose}>
          {t('common.closeBtn')}
        </button>
      }
    >
      <div className="space-y-3">
        {isError ? <ErrorState error={error} /> : null}
        {isLoading ? <SkeletonRows rows={6} /> : null}

        {!isLoading && data ? (
          rows.length === 0 ? (
            <EmptyState>{t('clientReliability.noSites')}</EmptyState>
          ) : (
            <>
              <p className="text-[11px] text-dim">
                {t('clientReliability.windowNote', { days: data.window_days })}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[11px]">
                  <thead>
                    <tr className="text-dim">
                      <th className="px-2 py-1 font-semibold">{t('clientReliability.col.client')}</th>
                      <th className="px-2 py-1 font-semibold">{t('clientReliability.col.site')}</th>
                      <th className="px-2 py-1 font-semibold">{t('clientReliability.col.delivered')}</th>
                      <th className="px-2 py-1 font-semibold">{t('clientReliability.col.gpsConfirmed')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.site_id}
                        className={`border-t border-line ${row.low_sample ? 'opacity-55' : ''}`}
                      >
                        <td className="px-2 py-1 font-semibold text-ink">{row.client_name}</td>
                        <td className="px-2 py-1">{row.site_name}</td>
                        <td className="px-2 py-1">{row.delivered_count}</td>
                        <td className="px-2 py-1">
                          <RateBadge rate={row.gps_confirmed_rate} lowSample={row.low_sample} t={t} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10.5px] text-dim">
                {t('clientReliability.lowSampleNote', { n: data.low_sample_threshold })}
              </p>
            </>
          )
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * A low-sample row is shown in the dim/neutral tone regardless of its
 * numeric rate — coloring a single manually-confirmed delivery red would
 * present "unmeasured" as if it were "confirmed bad", which is exactly the
 * distinction the backend's low_sample flag exists to preserve.
 */
function RateBadge({ rate, lowSample, t }) {
  if (rate === null || rate === undefined) {
    return <span className="text-dim">{t('clientReliability.noData')}</span>;
  }
  const pct = Math.round(rate * 100);
  const tone = lowSample ? 'text-dim' : pct >= 80 ? 'text-green' : pct >= 40 ? 'text-amber' : 'text-red';
  return (
    <span className={`font-semibold ${tone}`}>
      {pct}%{lowSample ? ` (${t('clientReliability.lowSampleBadge')})` : ''}
    </span>
  );
}
